import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { getSeoContent } from 'src/components/pageMeta/SEOMetaUtils';
import SeoContent from 'src/components/seoContent/SeoContent';
import SeoDescription from 'src/components/seoContent/SeoDescription';
import ThumbnailImage from 'src/components/thumbnail/components/ThumbnailImage';
import { ThumbnailTypes } from 'src/components/thumbnail/types';
import {
  CelebsMetaData,
  HeaderProps
} from 'src/services/responseTypes/celebsResponse';
import { thumbnailPropertySetter } from 'src/utils/analytics/mixpanel/propertySetter';
import {
  generateCardsUrl,
  navigationHandler
} from 'src/utils/cardsUrlGenerator';
import { fetchCelebFilmographyData } from 'src/utils/celebsUtils/celebsUtils';
import { DISPLAY_LANGUAGE_COOKIE } from 'src/utils/constants/cookieConstants';
import {
  CountryCode,
  DefaultContentLanguages
} from 'src/utils/constants/stringConstants';
import { TAG_NAME } from 'src/utils/constants/TagNameConstants';
import { getImageURL } from 'src/utils/ImageHelper/ImageURLGenerator';

import { useAppStore } from 'src/hooks/useAppStore';
import { CELEBRITY } from '../../../utils/constants/CelebsConstants';
import * as styledComponents from './celebStyle';

const Filmography: React.FC<HeaderProps> = ({
  celebData,
  renderContentTitle
}) => {
  const { name } = celebData || {};
  const [filmographyData, setFilmographyData] = useState<CelebsMetaData[]>([]);
  const [seoContentData, setSEOContentData] = useState<string>(String);
  const {
    initialLaunchData,
    appConfig,
    usersLocationData,
    usersPreferenceData
  } = useAppStore();
  const deviceWidth = appConfig?.deviceWidth || 0;
  const router = useRouter();
  const currentPath = router?.asPath || '';

  const [visibleThumbnails, setVisibleThumbnails] = useState(10);

  const { IN } = CountryCode;
  const celebQuery = router.query.celebName as string;
  // Regex is added to remove speacial characters in query and capitalize the celeb name
  const celebName = celebQuery.replace(
    /[^a-z0-9]*([a-z0-9])([a-z0-9]*)/g,
    (m, u, l) => u.toUpperCase() + l.toLowerCase() + ' '
  );

  const seoHeader = name
    ? `Top ${name} Movie Details:`
    : `Top ${celebName} Movie Details:`;

  const loadMore = () => {
    setVisibleThumbnails((prevVisible) => prevVisible + 10);
  };

  const reqObject = {
    query: celebData?.name || (celebQuery as string),
    country: usersLocationData?.country_code || IN,
    limit: 25,
    page: 0,
    filters: {},
    languages: usersPreferenceData?.content_language || DefaultContentLanguages,
    translation:
      usersPreferenceData?.display_language || DISPLAY_LANGUAGE_COOKIE
  };

  useEffect(() => {
    getFilmographyData();
    getSEOContentData();
  }, []);

  const getFilmographyData = async () => {
    const filmoData = await fetchCelebFilmographyData(
      initialLaunchData?.platform_token?.token,
      reqObject
    );
    if (filmoData) {
      setFilmographyData(filmoData);
    }
  };

  let latestData: CelebsMetaData[] = [];
  if (filmographyData && filmographyData.length) {
    latestData = filmographyData.slice(0, 5);
  }

  const getSEOContentData = async () => {
    const seoDataArray = await Promise.allSettled([getSeoContent(currentPath)]);

    const [seoData] = seoDataArray.map((resolvedData) => {
      if (resolvedData?.status === 'fulfilled') {
        return resolvedData?.value;
      }
    });
    const path = `/${currentPath?.split('/')[1].toString()}/${currentPath
      ?.split('/')[2]
      .toString()}`;
    const seoContent =
      path && seoData && Object.keys(seoData)?.length ? seoData[path] : null;

    if (seoContent) {
      setSEOContentData(seoContent?.html);
    }
  };

  return (
    <>
      {filmographyData?.length && (
        <>
          {renderContentTitle && renderContentTitle()}
          <div data-testid="thumbnail" css={styledComponents.ThumbnailStyle1}>
            {filmographyData
              .slice(0, visibleThumbnails)
              .map((item: CelebsMetaData, index: number) => {
                const { assetType, businessType, billingType } = item || {};

                const imageUrl = getImageURL({
                  deviceWidth,
                  tagName: TAG_NAME.PORTRAIT_SMALL,
                  item: item || {}
                });

                let slugData = generateCardsUrl(item) || '';
                slugData = slugData.startsWith('/') ? slugData : `/${slugData}`;

                const watchCallback = () => {
                  if (slugData) {
                    window.storeMixPanelMetricsInSS('CLICKED_EVT', Date.now());
                    thumbnailPropertySetter({ item: { ...item, imageUrl } });
                    navigationHandler(slugData, (slugData) =>
                      router.push(slugData, slugData)
                    );
                  }
                };

                return (
                  <div
                    data-testid="thumbnailImage"
                    key={index}
                    css={styledComponents.ThumbnailStyle}>
                    <ThumbnailImage
                      imageUrl={imageUrl || ''}
                      assetType={assetType}
                      slug={slugData || ''}
                      businessType={businessType || ''}
                      billingType={billingType || ''}
                      thumbnailType={ThumbnailTypes.vertical}
                      onClickHandler={watchCallback}
                    />
                  </div>
                );
              })}
          </div>
          {visibleThumbnails < filmographyData.length && (
            <div
              data-testid="loadMore"
              css={styledComponents.LoadMore}
              onClick={loadMore}>
              {CELEBRITY.LOAD_MORE}
            </div>
          )}

          {currentPath?.includes('/filmography') && (
            <>
              <div data-testid="filmographySEO" css={styledComponents.CelebSEO}>
                <SeoDescription
                  seoDescription={seoContentData}
                  pageType={CELEBRITY.CELEBS}
                />
                <SeoContent
                  seoData={null}
                  header={seoHeader}
                  latestData={latestData}
                  pageType={CELEBRITY.CELEBS}
                  popularData={latestData}
                  actor={celebName}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default Filmography;
